import React, { useEffect, useState } from "react"
import MarkAsDone from "../../../components/Orders/Helpers/MarkAsDone"
import { Col, Descriptions, Popover, Row, Table, Typography } from "antd"
import { OrderAndPrefix } from "../../../components/Orders/Helpers/OrderAndPrefix"
import { dueDateTag, endDate, startDate } from "../../../components/Orders/Helpers/TableDetails"
import moment from "moment"
import { collection, onSnapshot, query, where } from "firebase/firestore"
import { db } from "../../../firebase/config"
import PageHeader from "../../../components/Elements/PageHeader"
import { ClockCircleOutlined } from "@ant-design/icons"

const { Text, Link } = Typography

export default function PendingOrders() {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [removingOrderIds, setRemovingOrderIds] = useState([]) // Define the state here

  useEffect(() => {
    setLoading(true)
    const ordersQuery = query(
      collection(db, "orders"),
      where("orderStatus", "==", 6),
      where("hasInvoice", "!=", true)
    )

    const unsubscribe = onSnapshot(ordersQuery, (snapshot) => {
      let results = []
      snapshot.docs.forEach((doc) => {
        results.push({ ...doc.data(), id: doc.id })
      })

      // Filter orders: only keep orders where uid equals the specified uid
      const filteredResults = results.filter(
        (order) => order.uid !== "E1XNaWvT6PccXRSKB3JNlNehI362"
      )

      // Process each order: compute a unified end timestamp object
      const processedData = filteredResults.map((order) => {
        // Pick the available end timestamp (doneByRewinder or doneByTape)
        const unifiedEndSeconds = order.doneByRewinder?.seconds || order.doneByTape?.seconds || null
        // Create an object with a seconds property for use with moment.unix
        const unifiedEnd = unifiedEndSeconds ? { seconds: unifiedEndSeconds } : null

        // For created, if order.created is a Firestore timestamp it should already have seconds.
        const unifiedCreated = order.created

        return { ...order, unifiedEnd, unifiedCreated }
      })

      // Sort descending by unifiedEnd (latest first)
      processedData.sort((a, b) => {
        if (a.unifiedEnd && b.unifiedEnd) {
          return b.unifiedEnd.seconds - a.unifiedEnd.seconds
        }
        if (!a.unifiedEnd) return 1
        if (!b.unifiedEnd) return -1
        return 0
      })

      setData(processedData)
      setLoading(false)
    })

    return () => unsubscribe()
  }, [])

  //client filter
  let clientFIlters =
    data &&
    data.reduce((accumulator, item) => {
      const clientName = item.client.client
      const existingItem = accumulator.find((filterItem) => filterItem.text === clientName)

      if (existingItem) {
        existingItem.value = clientName
      } else {
        accumulator.push({
          text: clientName,
          value: clientName,
        })
      }

      return accumulator
    }, [])

  let productFilters =
    data &&
    data.reduce((accumulator, item) => {
      const productName = item.selectedProduct.product.productName
      const existingItem = accumulator.find((filterItem) => filterItem.text === productName)

      if (existingItem) {
        existingItem.value = productName
      } else {
        accumulator.push({
          text: productName,
          value: productName,
        })
      }

      return accumulator
    }, [])

  let managerFilters =
    data &&
    data.reduce((accumulator, item) => {
      const manager = item.user // assuming 'user' represents the manager here
      if (manager && !accumulator.some((filterItem) => filterItem.text === manager)) {
        accumulator.push({
          text: manager,
          value: manager,
        })
      }
      return accumulator
    }, [])

  //default columns for table
  const defaultColumns = [
    {
      title: "Nr.",
      dataIndex: "orderNumber",
      align: "center",
      sorter: (a, b) => a.orderNumber - b.orderNumber,
      width: "10%",
      render: (value, record) => OrderAndPrefix(record),
    },
    {
      title: "Client",
      dataIndex: ["client", "client"],
      align: "center",
      width: "15%",
      filters: clientFIlters,
      onFilter: (value, record) => record.client.client.startsWith(value),
      filterSearch: true,
      ellipsis: true,
      render: (value) => <Text copyable={{ text: value }}>{value}</Text>,
    },
    {
      title: "Product",
      dataIndex: ["selectedProduct", "product", "productName"],
      align: "center",
      filters: productFilters,
      onFilter: (value, record) => record.selectedProduct.product.productName === value,
      filterSearch: true,
      width: "20%",
      render: (value) => <Text copyable={{ text: value }}>{value}</Text>,
    },
    {
      title: "Manager",
      dataIndex: "user",
      align: "center",
      ellipsis: true,
      filters: managerFilters,
      onFilter: (value, record) => record.user && record.user.includes(value),
    },
    {
      title: "Box",
      dataIndex: "boxesCount",
      align: "center",
      width: "4%",
      render: (value, record) =>
        record.selectedProduct.machine === "Tape" ? (
          <Text>{record.boxCount}</Text>
        ) : (
          <Text>{value}</Text>
        ),
    },
    {
      title: "Count",
      dataIndex: "amountCreated",
      align: "center",
      render: (value, record) => {
        if (record.selectedProduct.machine === "Tape") {
          return <Text copyable={{ text: record.rollCount }}>{record.rollCount} Rolls</Text>
        } else {
          return (
            <Popover
              content={
                <div style={{ padding: 5 }}>
                  <Descriptions column={1} style={{ width: 150 }}>
                    <Descriptions.Item label="Ordered">{record.labelCount}</Descriptions.Item>
                    <Descriptions.Item label="Created">{value}</Descriptions.Item>
                  </Descriptions>
                </div>
              }
              title="Amount details"
              trigger="click">
              <Link>{value}</Link>
            </Popover>
          )
        }
      },
    },
    {
      title: "Technical",
      dataIndex: "technicalPage",
      align: "center",
      render: (value) => (
        <Link target="_blank" href={value}>
          Link
        </Link>
      ),
    },
    {
      title: "Machine",
      dataIndex: ["selectedProduct", "machine"],
      align: "center",
      width: "5%",
    },
    {
      title: "Started",
      dataIndex: "unifiedCreated",
      align: "center",
      sorter: (a, b) => a.unifiedCreated.seconds - b.unifiedCreated.seconds,
      render: (value) => startDate(value),
    },
    {
      title: "Ended",
      dataIndex: "unifiedEnd",
      align: "center",
      sorter: (a, b) => a.unifiedEnd.seconds - b.unifiedEnd.seconds,
      render: (value, record) => endDate(value, record),
    },
    {
      title: "Due date",
      dataIndex: "orderDueDate",
      align: "center",
      sorter: (a, b) => a.orderDueDate.seconds - b.orderDueDate.seconds,
      render: (value) => <Text>{dueDateTag(value)}</Text>,
    },
  ]

  return (
    <div style={{ padding: 15 }}>
      <Row>
        <Col span={24}>
          <PageHeader icon={<ClockCircleOutlined />} text="Pending - Awaiting Document" />
        </Col>
        <Col span={24}>
          <Table
            loading={loading}
            style={{ marginTop: "15px" }}
            size="small"
            columns={defaultColumns}
            bordered
            dataSource={data}
            rowClassName={(record) => (removingOrderIds.includes(record.id) ? "fade-out" : "")}
            rowKey={(record) => record.id}
            pagination={{ defaultPageSize: "12", showSizeChanger: true }}
          />
        </Col>
      </Row>
    </div>
  )
}
